import { Brand, Config, Effect, Exit, Option, Schema, SchemaGetter, String as Str } from 'effect';

import { ConfigKey } from './keys';

const GTM_ID_PATTERN = /^GTM-[A-Z0-9]+$/;
const GTM_PLACEHOLDER = 'GTM-XXXXXX';

export const GoogleSiteVerificationConfig = Config.schema(
  Schema.Trim,
  ConfigKey.GoogleSiteVerification
).pipe(Config.option, Config.map(Option.filter(Str.isNonEmpty)));

export type GoogleTagManagerId = string & Brand.Brand<'GoogleTagManagerId'>;

export const GoogleTagManagerId = Brand.nominal<GoogleTagManagerId>();

export const GoogleTagManagerIdSchema = Schema.String.check(
  Schema.isPattern(GTM_ID_PATTERN, {
    message:
      'Google Tag Manager ID must match format GTM-XXXXXXX (GTM- followed by alphanumeric characters)',
  }),
  Schema.makeFilter((id) => id !== GTM_PLACEHOLDER, {
    message: 'Placeholder GTM-XXXXXX is not a valid Google Tag Manager ID',
  })
).pipe(Schema.fromBrand('GoogleTagManagerId', GoogleTagManagerId));

export type GoogleTagManagerIdFromSchema = Schema.Schema.Type<typeof GoogleTagManagerIdSchema>;

// v4: `Schema.decodeTo(target, transformation)` takes `{decode, encode}` where
// each is a `Getter`, not the v3 `SchemaTransformation.transform(...)` wrapper.
const OptionalGoogleTagManagerIdSchema = Schema.Trim.pipe(
  Schema.decodeTo(Schema.Option(GoogleTagManagerIdSchema), {
    decode: SchemaGetter.transform((trimmed: string) => {
      if (trimmed === '') {
        return Option.none<GoogleTagManagerIdFromSchema>();
      }
      const result = Schema.decodeExit(GoogleTagManagerIdSchema)(trimmed);
      return Exit.match(result, {
        onFailure: () => Option.none<GoogleTagManagerIdFromSchema>(),
        onSuccess: (validId) => Option.some(validId),
      });
    }),
    encode: SchemaGetter.transform((opt: Option.Option<string>) =>
      Option.match(opt, {
        onNone: () => '',
        onSome: (id) => id,
      })
    ),
  })
);

export const GoogleTagManagerIdConfig = Config.schema(
  OptionalGoogleTagManagerIdSchema,
  ConfigKey.GoogleTagManagerId
).pipe(Config.withDefault(Option.none<GoogleTagManagerIdFromSchema>()));

export const getGoogleTagManagerId = Effect.gen(function* () {
  const option = yield* GoogleTagManagerIdConfig;

  // v4: `Severity` no longer includes "None" — skip logging in non-prod via a
  // plain `if` rather than a no-op log level.
  if (Option.isNone(option) && process.env.NODE_ENV === 'production') {
    yield* Effect.logWarning('⚠️ GTM ID is not configured - Google Tag Manager will not be loaded');
  }

  return option;
});
